// SaveIntent — the single typed description of one save attempt.
//
// Every save entry point on PrescriptionDetailsPage constructs one of these
// instead of passing an ad-hoc positional argument list. It is the thing a
// deferred gate (positive-ID, generic-prompt, equivalents, dispense-error) stashes
// and resumes with — so it must carry finish / goNextRx / stayOnPage /
// billPlanCallback across a deferral, rather than being re-derived at the resume point.
//
// This module is types + a defaulting factory only; no save logic lives here.

import { Prescription } from '@/models/Prescription'

/** Callback handed in by InsuranceBillingComponent's save-and-bill flow. */
export type BillPlanCallback = (rx: Prescription | null, failed?: boolean) => void

export interface SaveIntent {
    /** Finish creating new Rxs for the same ingestion image (drives post-save navigation). */
    finish: boolean
    /** Save then advance to the next Rx in the batch. */
    goNextRx: boolean
    /** Save without navigating away or clearing the form. */
    stayOnPage: boolean
    /**
     * Dispense-error id being resolved as part of this save; null otherwise. Set by
     * modalSaveAndResolve and consumed by the orchestrator ONLY after a successful save
     * (ports.api.resolveDispenseError), so a failed save can never leave the error resolved
     * against an unsaved Rx.
     */
    resolveDispenseErrorId: number | null
    /** Present on the save-and-bill path; invoked with the saved Rx (or null + failed on error). */
    billPlanCallback: BillPlanCallback | null
}

/** Build a SaveIntent, defaulting every field; override only what the entry point sets. */
export function createSaveIntent(overrides: Partial<SaveIntent> = {}): SaveIntent {
    return {
        finish: false,
        goNextRx: false,
        stayOnPage: false,
        resolveDispenseErrorId: null,
        billPlanCallback: null,
        ...overrides,
    }
}
